import { createStyles, makeStyles } from "@material-ui/core";
import * as React from "react";

import { ChipCollection } from ".";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IChip,
    Input,
    RelodeTheme,
    ScrollContainer,
} from "../";
import { useAsyncEffect, useDebounce } from "../hooks";
import { pascalCase } from "../utils";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {},
            paper: {
                overflow: "visible",
            },
            dialogTitle: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
            },
            dialogContent: {
                overflow: "visible",
            },
            chipMatchCollectionWrapper: {
                marginTop: theme.spacing(2),
            },
            searchInput: {
                margin: theme.spacing(0, 0, 3, 0),
            },
            scrollContainer: {
                height: "250px",
            },
        }),
    { name: "ChipSelectionDialog" }
);

interface ChipSelectionDialogProps {
    open: boolean;
    baseId: string;
    chipGroups?: IChip[];
    context: string;
    description?: string;
    closeOnAdd?: boolean;
    singleSelect?: boolean;
    groupSelect?: boolean;
    onClose: () => void;
    onAdd: (selectedChips: IChip[]) => void;
    searchSuggestions: (
        searchText: string,
        groupsIds: number[],
        selectedChips?: IChip[]
    ) => any;
    pruneSelectedChips: (selectedChips?: IChip[]) => IChip[];
}

const getInitialState = (groups: IChip[]) => ({
    selectedChips: [],
    groupsChips: groups,
    matchableChips: [],
    currentSearchText: "",
});

export const ChipSelectionDialog = (props: ChipSelectionDialogProps) => {
    const cleared = React.useRef(false);
    const {
        closeOnAdd,
        context,
        description,
        singleSelect,
        groupSelect,
        onAdd,
        onClose,
        open,
        chipGroups,
        pruneSelectedChips,
        searchSuggestions,
    } = props;

    const classes = useStyles({});
    const [selectedChips, setSelectedChips] = React.useState<IChip[]>([]);
    const [groupsChips, setGroupsChips] = React.useState<IChip[]>(chipGroups);
    const [matchableChips, setMatchableChips] = React.useState<IChip[]>([]);
    const [searchText, setSearchText] = React.useState("");
    const [scrollContentKey, setScrollContentKey] = React.useState(0);

    const getInitialChipGroups = (chips: IChip[]) => {
        const initialChipArray = [];
        chips.forEach(chip => {
            chip.isMatched = false;
            initialChipArray.push(chip);
        });
        return initialChipArray;
    };

    const clearState = React.useCallback(() => {
        cleared.current = true;
        const initialChipGroups = getInitialChipGroups(chipGroups);
        setInitialState(initialChipGroups || []);
    }, [chipGroups]);

    const setInitialState = (groups: IChip[]) => {
        const newState = getInitialState(groups);
        setSelectedChips(newState.selectedChips);
        setGroupsChips(newState.groupsChips);
        setMatchableChips(newState.matchableChips);
        setSearchText(newState.currentSearchText);
    };

    const handleClose = React.useCallback(() => {
        setScrollContentKey(0);
        if (!cleared.current) {
            clearState();
        }
        onClose();
    }, [clearState, onClose]);

    const handleAdd = React.useCallback(() => {
        onAdd(selectedChips);
        setScrollContentKey(o => o + 1);
        // perhaps let the caller just simply close on add?
        // Nothing really prevents the caller from doing so already.
        closeOnAdd && handleClose();
    }, [onAdd, selectedChips, closeOnAdd, handleClose]);

    React.useEffect(() => {
        if (singleSelect && selectedChips && selectedChips.length > 0) {
            onAdd(selectedChips);
            setScrollContentKey(o => o + 1);
            // perhaps let the caller just simply close on add?
            // Nothing really prevents the caller from doing so already.
            closeOnAdd && handleClose();
        }
    }, [
        closeOnAdd,
        handleClose,
        onAdd,
        scrollContentKey,
        selectedChips,
        singleSelect,
    ]);

    useAsyncEffect(async () => {
        // #1 in mobile but this uses open and debounces load suggestions
        if (open) {
            setGroupsChips(getInitialChipGroups(chipGroups) || []);
            await debouncedLoadSuggestions("", selectedChips, groupsChips);

            setScrollContentKey(o => o + 1);
        }
    }, [open]);

    const loadSuggestions = async (
        _searchText: string,
        _selectedChips: IChip[],
        _groupsChips: IChip[]
    ) => {
        const groupIds: number[] = [];
        _groupsChips.forEach(gc => {
            if (gc.isMatched || _groupsChips.every(c => !c.isMatched)) {
                groupIds.push(gc.id);
            }
        });

        cleared.current = false;
        const mc = await searchSuggestions(
            _searchText,
            groupIds,
            _selectedChips
        );

        if (!mc) return;
        //remove selected chips if they are not in the suggestions list anymore
        setSelectedChips(pruneSelectedChips(_selectedChips));

        setMatchableChips(mc);
        setScrollContentKey(o => o + 1);
    };

    const debouncedLoadSuggestions = useDebounce(loadSuggestions, 500, []);

    const handleTagTypeToggle = async (id: number) => {
        const chips = [...groupsChips];
        const matchedChip = chips.find(chip => chip.id === id);

        // NOTE: this is different than the mobile version with dropdown as we can return with no selection here
        if (!matchedChip) return;

        matchedChip.isMatched = !matchedChip.isMatched;
        await debouncedLoadSuggestions(searchText, selectedChips, chips);

        setGroupsChips(chips);
        setScrollContentKey(o => o + 1);
    };

    const handleTagSelection = React.useCallback(
        (id: number) => {
            const matchableChipsCopy = { ...matchableChips };
            const selected = [...selectedChips];
            let matchedChip;

            Object.keys(matchableChipsCopy).forEach(type => {
                const chips = [...matchableChipsCopy[type]];
                const chip = chips.find(c => c.id === id);

                if (chip) {
                    chip.isMatched = !chip.isMatched;
                    matchedChip = chip;

                    const index = selected.findIndex(t => t.id === id);
                    if (matchedChip.isMatched) {
                        if (index < 0) {
                            selected.push(chip);
                        }
                    } else {
                        selected.splice(index, 1);
                    }
                }
            });
            setMatchableChips(matchableChipsCopy);
            setSelectedChips(selected);
        },
        [selectedChips, matchableChips]
    );

    const handleGroupSelection = React.useCallback(
        (ids: number[], groupId: number) => {
            const matchableChipsCopy = { ...matchableChips };
            const selected = [...selectedChips];
            const groupSelected = [...selectedChips].filter(
                x => x.groupId === groupId
            );
            const isAllSelected = groupSelected.length === ids.length;

            ids.forEach(id => {
                if (isAllSelected) {
                    Object.keys(matchableChipsCopy).forEach(type => {
                        const chips = [...matchableChipsCopy[type]];

                        const chip = chips.find(c => c.id === id);

                        if (chip) {
                            chip.isMatched = false;
                            const index = selected.findIndex(t => t.id === id);
                            selected.splice(index, 1);
                        }
                    });
                } else {
                    Object.keys(matchableChipsCopy).forEach(type => {
                        const chips = [...matchableChipsCopy[type]];

                        const chip = chips.find(c => c.id === id);

                        if (chip) {
                            chip.isMatched = true;
                            const index = selected.findIndex(t => t.id === id);
                            if (index < 0) {
                                selected.push(chip);
                            }
                        }
                    });
                }
            });
            setMatchableChips(matchableChipsCopy);
            setSelectedChips(selected);
        },
        [selectedChips, matchableChips]
    );

    const handleSearchTextChange = async (event: React.ChangeEvent<any>) => {
        const text = event.target.value;
        setSearchText(text);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        await debouncedLoadSuggestions(text, selectedChips, groupsChips);
    };

    const chipDisplay = React.useMemo(
        () =>
            groupsChips.map(groupChip => (
                <ChipCollection
                    id={`chipCollection-item-${pascalCase(
                        groupChip.name,
                        true
                    )}`}
                    key={groupChip.id}
                    description={
                        groupChip.isMatched ||
                        groupsChips.every(c => !c.isMatched)
                            ? groupChip.name
                            : undefined
                    }
                    chips={matchableChips[groupChip.id!]}
                    onChipClick={handleTagSelection}
                    groupSelect={groupSelect}
                    onGroupSelect={handleGroupSelection}
                />
            )),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [groupsChips, matchableChips]
    );

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md">
            <DialogTitle title={`add ${context}`} onClose={handleClose} />
            <DialogContent>
                {description && <>{description}</>}
                {!!chipGroups && (
                    <div className={classes.chipMatchCollectionWrapper}>
                        <ChipCollection
                            id={`chipCollection-item-${pascalCase(
                                context,
                                true
                            )}`}
                            description={`select ${context} groups to filter by`}
                            chips={groupsChips}
                            onChipClick={handleTagTypeToggle}
                        />
                    </div>
                )}
                <Input
                    fullWidth
                    autoFocus
                    className={classes.searchInput}
                    id="searchInput"
                    label={`type to filter ${context}`}
                    value={searchText}
                    onChange={handleSearchTextChange}
                />
                <ScrollContainer
                    className={classes.scrollContainer}
                    scrollContentKey={scrollContentKey.toString()}
                    showMoreIcon={false}
                >
                    {chipDisplay}
                </ScrollContainer>
            </DialogContent>
            {!singleSelect ? (
                <DialogActions
                    submit={{
                        label: "add",
                        onSubmit: handleAdd,
                        disabled: selectedChips.length === 0,
                    }}
                />
            ) : null}
        </Dialog>
    );
};
